import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Drawer, Form, Input, Select, Space, Typography } from 'antd';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

import { FilterSection } from '@app/components/tables/request_table/search_filter_bar/filter-section';
import { getRequestStatuses } from '@app/api';
import { useQueryState } from '@app/hooks/use-query-state';
import { Tools } from '@app/utils/tools';
import { LanguageContext } from '@app/global_provider';
import { i18n } from '@app/translations/i18n.config';
import { DATE_FORMAT } from '@app/config/constants';
import { FilterParamsEnum } from '@app/components/tables/request_table/search_filter_bar/filters.enum';
import { isShowTotalAmountPaid } from '@app/utils/permissions/permissions';
import { useAuth } from '@app/auth/auth-context';
import { RefundRequestStatusesEnum } from '@app/types';
import { getStatusOptionsByRole } from '@app/components/tables/request_table/search_filter_bar/utils';
import PresetSelect from '@app/components/tables/request_table/search_filter_bar/preset-select';
import { PresetActions } from '@app/components/tables/request_table/search_filter_bar/preset-actions';

import { ReactComponent as ArrowIcon } from '@app/assets/icons/arrow-rotate-left.svg';

const { RangePicker } = DatePicker;

interface FilterDrawerProps {
  isDrawerOpen: boolean;
  onClose: () => void;
}

export const FilterDrawer: FC<FilterDrawerProps> = ({ isDrawerOpen, onClose }) => {
  const { isRtl } = useContext<any>(LanguageContext);
  const { userRoles } = useAuth();
  const selectRef = useRef(null);

  const [urlParams, setUrlParams] = useQueryState();
  const [filterParams, setFilterParams] = useState(() => {
    const { search, presetId, ...otherParams } = urlParams;
    return otherParams;
  });
  const [currentPresetData, setCurrentPresetData] = useState({} as any);
  const [presetParams, setPresetParams] = useState(null);

  const { data: statuses, isLoading } = useQuery({
    queryKey: ['request_statuses'],
    queryFn: () => getRequestStatuses(),
  });

  const statusOptions = useMemo(() => {
    const isStatusAllowed = (statusKey) =>
      statusKey !== RefundRequestStatusesEnum.Draft && statusKey !== RefundRequestStatusesEnum.Cancelled;

    if (Tools.isPresent(statuses)) {
      const options = statuses
        ?.filter((s) => isStatusAllowed(s.key))
        .map((s) => ({
          label: s.name,
          value: s.key,
        }));

      return getStatusOptionsByRole(options, userRoles);
    } else {
      return null;
    }
  }, [statuses]);

  const isOriginPresetParamsUpdated = (params) => {
    return Tools.isPresent(currentPresetData) && JSON.stringify(currentPresetData?.metadata) !== JSON.stringify(params);
  };

  const updateUrlParamsHandler = (params) => {
    //TODO do refactor
    if (isOriginPresetParamsUpdated(params)) {
      setCurrentPresetData(null);
      const updatedParams = { ...urlParams, ...params, presetId: null };

      setUrlParams({ ...urlParams, ...updatedParams });
    } else {
      const updatedParams = { ...urlParams, ...presetParams, ...params, presetId: currentPresetData?.id };

      setUrlParams({ ...urlParams, ...updatedParams });
    }
  };

  const resetSelectValueHandler = () => {
    setCurrentPresetData(null);
    if (selectRef.current) {
      selectRef.current.setCurrentPresetId(null);
    }
  };

  const setSelectValueHandler = (value: string) => {
    setCurrentPresetData(null);
    if (selectRef.current && value) {
      selectRef.current.setCurrentPresetId(value);
    }
  };

  const resetParamsHandler = (params) => {
    if (params === null) {
      resetSelectValueHandler();
    }
    setFilterParams((prev) => {
      const updatedParams = { ...prev };

      const resetParam = (param) => {
        updatedParams[param] = Array.isArray(updatedParams[param]) ? [] : null;
      };

      const keysToReset = params || Object.values(FilterParamsEnum);
      keysToReset.forEach(resetParam);

      return updatedParams;
    });
  };

  const isFilterParamsEmpty = (params) => {
    return Object.values(params).every((p) => Tools.isBlank(p));
  };

  const isApplyParamsDisabled = () => {
    const { search, presetId, ...otherParams } = urlParams;
    if (Tools.isBlank(presetId) && Tools.isPresent(currentPresetData)) {
      return false;
    }
    const isEqual = JSON.stringify(filterParams) === JSON.stringify(otherParams);
    const isEmpty = isFilterParamsEmpty(filterParams) && isFilterParamsEmpty(otherParams);

    return isEqual || isEmpty;
  };

  return (
    <Drawer
      title={
        <Space className={'w-100 justify-content-between'}>
          <Typography.Title className={'my-1'} level={3}>
            {i18n.t('searchFilterBar.drawerTitle')}
          </Typography.Title>
          <Space>
            {isOriginPresetParamsUpdated(filterParams) && (
              <Button
                hidden={isFilterParamsEmpty(filterParams)}
                className={isRtl && 'flex-row-reverse'}
                size={'small'}
                type={'dashed'}
                icon={<ArrowIcon className={isRtl && 'me-2 ms-0'} />}
                onClick={() => setFilterParams(currentPresetData?.metadata)}
              >
                {i18n.t('searchFilterBar.restorePreset')}
              </Button>
            )}
            <Button
              hidden={isFilterParamsEmpty(filterParams)}
              className={isRtl && 'flex-row-reverse'}
              size={'small'}
              type={'dashed'}
              icon={<ArrowIcon className={isRtl && 'me-2 ms-0'} />}
              onClick={() => resetParamsHandler(null)}
            >
              {i18n.t('searchFilterBar.resetAll')}
            </Button>
          </Space>
        </Space>
      }
      placement={isRtl ? 'left' : 'right'}
      open={isDrawerOpen}
      onClose={onClose}
      footer={
        <Space className={'w-100 justify-content-between'}>
          <PresetActions
            filters={filterParams}
            currentPreset={currentPresetData}
            isSaveDisabled={isFilterParamsEmpty(filterParams)}
            isPresetSelected={Tools.isPresent(currentPresetData)}
            isOriginParamsUpdated={isOriginPresetParamsUpdated(filterParams)}
            onSuccessCreated={setSelectValueHandler}
            resetFilters={() => resetParamsHandler(null)}
          />
          <Button
            type={'primary'}
            disabled={isApplyParamsDisabled()}
            onClick={() => {
              updateUrlParamsHandler(filterParams);
              onClose();
            }}
          >
            {i18n.t('searchFilterBar.buttons.applyFilters')}
          </Button>
        </Space>
      }
    >
      <Form colon={false}>
        <FilterSection
          title={i18n.t('searchFilterBar.preset.title')}
          hidden={Tools.isBlank(currentPresetData)}
          resetHandler={resetSelectValueHandler}
        >
          <PresetSelect
            ref={selectRef}
            setPresetData={(data) => {
              setCurrentPresetData(data);
              setFilterParams(data?.metadata || {});
            }}
            filters={filterParams}
            updatePresetParams={setPresetParams}
          />
        </FilterSection>

        <FilterSection
          title={i18n.t('searchFilterBar.date.title')}
          hidden={Tools.isBlank(filterParams.minDate) && Tools.isBlank(filterParams.maxDate)}
          resetHandler={() => resetParamsHandler([FilterParamsEnum.MinDate, FilterParamsEnum.MaxDate])}
        >
          <RangePicker
            className={'w-100'}
            separator={i18n.t('searchFilterBar.from')}
            suffixIcon={i18n.t('searchFilterBar.to')}
            value={[
              (filterParams.minDate ? dayjs(filterParams.minDate) : null) as any,
              (filterParams.maxDate ? dayjs(filterParams.maxDate) : null) as any,
            ]}
            placeholder={[
              i18n.t('searchFilterBar.date.startPlaceholder'),
              i18n.t('searchFilterBar.date.endPlaceholder'),
            ]}
            onChange={([minDate, maxDate]) => {
              setFilterParams({
                ...filterParams,
                minDate: dayjs(minDate.toDate()).format(DATE_FORMAT),
                maxDate: dayjs(maxDate.toDate()).format(DATE_FORMAT),
              });
            }}
            format={DATE_FORMAT}
          />
        </FilterSection>

        <FilterSection
          title={i18n.t('searchFilterBar.statuses.title')}
          hidden={Tools.isBlank(filterParams.statuses)}
          resetHandler={() => resetParamsHandler([FilterParamsEnum.Statuses])}
        >
          <Select
            optionFilterProp={'label'}
            options={statusOptions}
            placeholder={i18n.t('searchFilterBar.statuses.placeholder')}
            mode="multiple"
            value={Tools.wrapArray(filterParams.statuses)}
            dropdownClassName={'text-upper-case'}
            loading={isLoading}
            className={'w-100 text-upper-case'}
            onChange={(values) => setFilterParams({ ...filterParams, statuses: values })}
          />
        </FilterSection>

        <FilterSection
          title={i18n.t('searchFilterBar.amount.title')}
          hidden={Tools.isBlank(filterParams.minTotalAmount) && Tools.isBlank(filterParams.maxTotalAmount)}
          resetHandler={() => resetParamsHandler([FilterParamsEnum.MinTotalAmount, FilterParamsEnum.MaxTotalAmount])}
        >
          <Space>
            <Form.Item label={<Typography className={'m-0'}>{i18n.t('searchFilterBar.from')}</Typography>}>
              <Input
                type={'number'}
                min={0}
                placeholder={'0'}
                value={parseInt(filterParams?.minTotalAmount)}
                onChange={(e) => setFilterParams({ ...filterParams, minTotalAmount: e.target.value })}
              />
            </Form.Item>
            <Form.Item label={<Typography className={'m-0'}>{i18n.t('searchFilterBar.to')}</Typography>}>
              <Input
                type={'number'}
                min={0}
                placeholder={'1000'}
                value={parseInt(filterParams?.maxTotalAmount)}
                onChange={(e) => setFilterParams({ ...filterParams, maxTotalAmount: e.target.value })}
              />
            </Form.Item>
          </Space>
        </FilterSection>

        {isShowTotalAmountPaid(userRoles) && (
          <FilterSection
            title={i18n.t('searchFilterBar.amountPaid.title')}
            hidden={Tools.isBlank(filterParams.minTotalAmountPaid) && Tools.isBlank(filterParams.maxTotalAmountPaid)}
            resetHandler={() =>
              resetParamsHandler([FilterParamsEnum.MinTotalAmountPaid, FilterParamsEnum.MaxTotalAmountPaid])
            }
          >
            <Space>
              <Form.Item label={<Typography className={'m-0'}>{i18n.t('searchFilterBar.from')}</Typography>}>
                <Input
                  type={'number'}
                  min={0}
                  placeholder={'0'}
                  value={parseInt(filterParams?.minTotalAmountPaid)}
                  onChange={(e) => setFilterParams({ ...filterParams, minTotalAmountPaid: e.target.value })}
                />
              </Form.Item>
              <Form.Item label={<Typography className={'m-0'}>{i18n.t('searchFilterBar.to')}</Typography>}>
                <Input
                  type={'number'}
                  min={0}
                  placeholder={'1000'}
                  value={parseInt(filterParams?.maxTotalAmountPaid)}
                  onChange={(e) => setFilterParams({ ...filterParams, maxTotalAmountPaid: e.target.value })}
                />
              </Form.Item>
            </Space>
          </FilterSection>
        )}
      </Form>
    </Drawer>
  );
};
